import { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { useRecoilValue } from 'recoil';
import { useQueryClient } from '@tanstack/react-query';

import { deleteMember, inviteMember, syncTrainingDelegations, updateMember } from 'api/Member';

import { formatDate } from 'lib/helpers/dateUtils';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import Modal from 'components/shared/Modal/Modal';
import Button from 'components/shared/Button/Button';
import { showAlert } from 'components/shared/Alert/Alert';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import { currentPractice } from 'components/practice/practiceState';

import useNewStaffMemberFormik from '../lib/useNewStaffMemberFormik';
import { checkAvailableTimesError, getInviteMemberInitialValues } from '../lib/inviteMemberHelpers';

import StaffForm from './StaffForm';

const InviteMemberModal = ({ item: member, isOpen, handleClose }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const outletCtx = useOutletContext();
  const practice = useRecoilValue(currentPractice);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const currentPracticeId = outletCtx?.practice?.id || practice?.id;

  const buildPayload = (values) => {
    const { role, dob, availableTimes, trainings, ...user } = values;
    return {
      user: {
        ...user,
        dob: formatDate(dob),
        payment_rate: Math.round(parseFloat(values.payment_rate) * 100)
      },
      role_id: role,
      practice_id: currentPracticeId,
      ...(availableTimes && { available_times: JSON.stringify(availableTimes) }),
      trainings: trainings ?? []
    };
  };

  const syncDelegationsAndInvalidate = async (userId, trainings) => {
    await syncTrainingDelegations(navigate, {
      userId,
      practiceId: currentPracticeId,
      definitionIds: trainings ?? []
    });
    queryClient.invalidateQueries({
      queryKey: ['wizard-instance-delegation'],
      refetchType: 'all'
    });
  };

  const formik = useNewStaffMemberFormik(
    getInviteMemberInitialValues(member),
    async (values, formikHelpers) => {
      if (member) {
        const errorMessage = checkAvailableTimesError(values.availableTimes);
        if (errorMessage) {
          showAlert({ color: 'danger', message: errorMessage });
          return;
        }
      }

      const { trainings, ...payload } = buildPayload(values);

      if (!member) {
        const result = await createAsync(payload);
        if (result?.code !== 0 || !result?.new_user_id) return;
        await syncDelegationsAndInvalidate(result.new_user_id, trainings);
      } else {
        const result = await updateAsync({
          ...payload,
          user: { ...payload.user, id: member.user_id }
        });
        if (result?.code !== 0) return;
        await syncDelegationsAndInvalidate(member.user_id, trainings);
      }
      formikHelpers.resetForm();
      handleClose();
    }
  );

  const { mutateAsync: createAsync, isPending } = useCustomMutation({
    mutationFn: (params) =>
      inviteMember(navigate, {
        ...params,
        practice_id: currentPracticeId
      }),
    successMessage: 'Staff member invited',
    invalidateQueryKey: ['member', 'practice']
  });

  const { mutateAsync: updateAsync, isPending: isUpdatePending } = useCustomMutation({
    mutationFn: (params) =>
      updateMember(navigate, {
        ...params,
        practice_id: currentPracticeId
      }),
    successMessage: 'Staff member updated',
    invalidateQueryKey: ['member', 'practice']
  });

  const mutateDelete = useCustomMutation({
    mutationFn: () => {
      if (!member?.user_id) return Promise.resolve({ code: 0 });
      return deleteMember(navigate, {
        memberId: member.user_id,
        practiceId: currentPracticeId
      });
    },
    invalidateQueryKey: ['member', 'practice'],
    onSuccess: (res) => {
      if (res?.code === 0) {
        queryClient.invalidateQueries(['member']);
        showAlert({ title: 'Member deleted', color: 'success' });
      } else {
        showAlert({ title: "Couldn't delete the member", color: 'danger' });
      }
      setShowConfirmDelete(false);
      handleClose();
    }
  });

  const closeModal = () => {
    formik.resetForm();
    handleClose();
  };

  return (
    <>
      <Modal
        handleOpen={isOpen}
        handleClose={closeModal}
        footerActions={{
          other: member ? (
            <Button
              variant="link"
              color="neutral"
              icon="trash-mono"
              text="Delete staff member"
              size="small"
              onClick={() => setShowConfirmDelete(true)}
            />
          ) : null,
          primary: {
            text: member ? 'Update' : 'Invite',
            onClick: formik.handleSubmit,
            loading: isPending || isUpdatePending
          }
        }}
        title={`${member ? 'Edit' : 'Invite'} staff member`}>
        <StaffForm formik={formik} practiceId={currentPracticeId} staffMemberId={member?.user_id} />
      </Modal>
      {showConfirmDelete && (
        <Confirm
          variant="danger"
          title="Delete member"
          icon="trash-mono"
          message="Are you sure you want to delete this member?"
          handleOpen={showConfirmDelete}
          handleClose={() => setShowConfirmDelete(false)}
          footerActions={{
            primary: {
              text: 'Delete',
              onClick: () => mutateDelete.mutate(),
              color: 'danger',
              isLoading: mutateDelete.isLoading
            }
          }}
        />
      )}
    </>
  );
};

export default InviteMemberModal;
